import { useId, useState } from "react";

import { Disclosure, DisclosureButton, DisclosurePanel } from "@headlessui/react";
import { observer } from "mobx-react-lite";
import { useTranslations } from "next-intl";

import { CollapsedPopover } from "@/components/doc/right-sidebar/CollapsedPopover";
import { NodeConstant } from "@/components/doc/right-sidebar/node-viewer/NodeConstant";
import { NodeFunction } from "@/components/doc/right-sidebar/node-viewer/NodeFunction";
import {
  NodeShortView,
  useNodeShortViewLabel,
} from "@/components/doc/right-sidebar/node-viewer/NodeShortView";
import { NodeVariable } from "@/components/doc/right-sidebar/node-viewer/NodeVariable";
import { type ViewerNode } from "@/components/doc/right-sidebar/node-viewer/types";
import { useHintHover } from "@/components/hints/useHintHover";
import { Icon } from "@/components/icons/Icon";
import { twMerge } from "@/utils/twMerge";

import { getGroupObjects } from "./node-viewer/utils";

import type { DocumentModel } from "@/models/DocumentModel";

type NodeRendererProps = {
  node: ViewerNode;
  ids: Set<string>;
};

const NodeRenderer = observer(function NodeRenderer({ node, ids }: NodeRendererProps) {
  switch (node.type) {
    case "variable": {
      return <NodeVariable node={node} ids={ids} />;
    }

    case "constant": {
      return <NodeConstant node={node} ids={ids} />;
    }

    case "function": {
      return <NodeFunction node={node} ids={ids} />;
    }

    default: {
      return null;
    }
  }
});

export const GROUPS_COLLAPSE_LIMIT = 6;

/**
 * One object: a toggle over its whole row, which names it, and its properties as a group of
 * labelled fields. The row's insert button sits above the toggle rather than inside it.
 */
const ObjectItem = observer(function ObjectItem({
  node,
  ids,
  defaultOpen,
}: {
  node: ViewerNode;
  ids: Set<string>;
  defaultOpen: boolean;
}) {
  const toggleId = useId();
  const label = useNodeShortViewLabel(node);

  return (
    <Disclosure as="div" defaultOpen={defaultOpen} role="listitem">
      {({ open }) => (
        <>
          <div className="relative group">
            <DisclosureButton
              id={toggleId}
              aria-label={label}
              className="peer absolute inset-0 w-full rounded-8 cursor-pointer"
            />
            {/* TODO: node grouping */}
            <NodeShortView node={node} isActive={open} />
          </div>
          <DisclosurePanel className="pl-8" role="group" aria-labelledby={toggleId}>
            <NodeRenderer node={node} ids={ids} />
          </DisclosurePanel>
        </>
      )}
    </Disclosure>
  );
});

const Content = observer(function Content({ doc }: { doc: DocumentModel }) {
  const t = useTranslations("sidebar");
  const [isExpanded, setIsExpanded] = useState(false);
  const groups = getGroupObjects(doc);

  if (groups.length === 0) {
    return null;
  }

  const isSelected = doc.selection.hasSelectedNodes;
  const isOpen = isSelected && groups.length === 1;

  const exceedsListLimit = groups.length > GROUPS_COLLAPSE_LIMIT;
  const visibleGroups =
    exceedsListLimit && !isExpanded ? groups.slice(0, GROUPS_COLLAPSE_LIMIT) : groups;

  return (
    <>
      {visibleGroups.map((group) => {
        const id = group.ids.values().next().value!;
        return (
          <ObjectItem
            key={`${id}-${isOpen}`}
            node={doc.nodes.get(id)! as ViewerNode}
            ids={group.ids}
            defaultOpen={isOpen}
          />
        );
      })}
      {exceedsListLimit && (
        <button
          type="button"
          className="w-full text-sm text-blue-500 text-left min-h-9 pl-5"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          {isExpanded ? t("nodeProps.showLess") : t("nodeProps.showMore", { count: groups.length })}
        </button>
      )}
    </>
  );
});

type Props = {
  doc: DocumentModel;
  isCollapsed?: boolean;
};

export const NodeProps = observer(function NodeProps({ doc, isCollapsed }: Props) {
  const t = useTranslations("sidebar");
  const groups = getGroupObjects(doc);
  const hintHandlers = useHintHover("objectsTree");
  const isSelected = doc.selection.hasSelectedNodes;

  if (groups.length === 0) {
    return null;
  }

  if (isCollapsed) {
    return (
      <div className="flex flex-col gap-1 rounded-8 p-0.75 bg-gray-100 dark:bg-gray-900">
        <CollapsedPopover
          icon={<Icon name="Objects" />}
          tooltipTitle={t("nodeProps.objects", { count: groups.length })}
          title={t("nodeProps.objects", { count: groups.length })}
          contentClassName="bg-document-bg dark:bg-document-bg"
        >
          <div role="list" aria-label={t("nodeProps.objects", { count: groups.length })}>
            <Content doc={doc} />
          </div>
        </CollapsedPopover>
      </div>
    );
  }

  return (
    <div data-panel="node_props">
      <Disclosure defaultOpen>
        {({ open: groupOpen }) => (
          <div className="flex-col py-0">
            <DisclosureButton
              id="node-props-title"
              disabled={isSelected}
              className={twMerge(
                "font-mono text-left pt-1.5 px-1.5 w-full uppercase font-light text-gray-500 dark:text-gray-400",
                !groupOpen && "pb-1.25",
                !isSelected && "hover:text-black dark:hover:text-white",
              )}
              {...hintHandlers}
            >
              <span className="text-xxs">{t("nodeProps.objects", { count: groups.length })}</span>
            </DisclosureButton>
            <DisclosurePanel
              className={twMerge("pb-0.5", groupOpen && "pb-1.25")}
              static={isSelected}
              role="list"
              aria-labelledby="node-props-title"
            >
              <Content doc={doc} />
            </DisclosurePanel>
          </div>
        )}
      </Disclosure>
    </div>
  );
});
